import { Box, TextField, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DateFormatsEnum } from '../../../constants/date';
import { BUSINESS_REASON_ERROR_TOOLTIP_TEXT, ON_HOLD_ERROR_TOOLTIP_TEXT } from '../../../constants/oneLink';
import { BUSINESS_REASON_ID } from '../../../constants/reasons';
import { IOneLinkErrors, OneLinkChunksEnum } from '../../../contexts/OneLinkSummaryProvider';
import { useCampaignSummaries } from '../../../service/InventoryService';
import { selectBusinessReasons } from '../../../store/selectors/reasons';
import { IOneLinkWithCampaignSummaries } from '../../../types/oneLink';
import { formatDate, isValidDate } from '../../../utils/date';
import { isRealCampaignId } from '../../../utils/marketplaceSend';
import { isDigitalPYGReport, isDigitalReport } from '../../../utils/reports';
import { Label, LabeledField, Section } from './common';

interface IProps {
  onEdit?: () => void;
  instance: IOneLinkWithCampaignSummaries | null | undefined;
  errors?: IOneLinkErrors;
}

export const AdditionalSection: React.FC<IProps> = ({ onEdit, errors, instance }) => {
  const reasons = useSelector(selectBusinessReasons);

  const isDigitalOL = useMemo(
    () =>
      isDigitalReport(instance as IOneLinkWithCampaignSummaries) ||
      isDigitalPYGReport(instance as IOneLinkWithCampaignSummaries),
    [instance],
  );

  const onHoldUntilError = useMemo(() => errors?.[OneLinkChunksEnum.AdditionalInfo]?.on_hold_until, [errors]);
  const businessReasonError = useMemo(() => errors?.[OneLinkChunksEnum.AdditionalInfo]?.business_reason_id, [errors]);

  const businessReason = React.useMemo(() => {
    return reasons?.find((reason) => reason.uid === instance?.[BUSINESS_REASON_ID]);
  }, [reasons, instance]);

  const { data: singleCampaignSummary } = useCampaignSummaries(
    { campaign_ids: instance?.pre_created_engagement_id ? [instance.pre_created_engagement_id] : undefined },
    isRealCampaignId(instance?.pre_created_engagement_id),
  );

  const isBusinessReasonRequired = useMemo(() => {
    return Boolean(singleCampaignSummary?.some((item) => item.business_reason_required));
  }, [singleCampaignSummary]);

  const onHoldUntil = useMemo(() => {
    if (instance?.on_hold_until && isValidDate(new Date(instance?.on_hold_until))) {
      return formatDate(instance?.on_hold_until, DateFormatsEnum.ddMMMyyyy);
    }
    return 'Date not set';
  }, [instance?.on_hold_until]);

  const additionalSectionErrors = useMemo(() => {
    return businessReasonError || onHoldUntilError;
  }, [businessReasonError, onHoldUntilError]);

  return (
    <Section
      title="Additional"
      onEdit={onEdit}
      minHeight={420}
      errors={additionalSectionErrors}
      tooltipText={
        (onHoldUntilError && ON_HOLD_ERROR_TOOLTIP_TEXT) || (businessReasonError && BUSINESS_REASON_ERROR_TOOLTIP_TEXT)
      }
    >
      <Box sx={{ height: 300 }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1, gap: '94px', position: 'relative' }}>
            <LabeledField
              label="On hold until"
              value={onHoldUntil}
              error={Boolean(onHoldUntilError)}
              errorTooltip={ON_HOLD_ERROR_TOOLTIP_TEXT}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
            <Label>Sender notifications</Label>
            <Typography>{instance?.disable_sender_email_notifications ? 'Disabled' : 'Enabled'}</Typography>
          </Box>
          {!isDigitalOL && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
              <Label>Tracking notification</Label>
              <Typography>{instance?.disable_recipient_shipped_email ? 'Disabled' : 'Enabled'}</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ position: 'relative' }}>
          <TextField
            label="Business reason"
            variant="outlined"
            error={!!businessReasonError}
            value={businessReason?.name ?? ''}
            aria-readonly={true}
            sx={{
              mb: 0,
              '& .MuiInputLabel-root': {
                color: isBusinessReasonRequired && !businessReason?.name ? 'error.main' : undefined,
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: isBusinessReasonRequired && !businessReason?.name ? 'error.main' : undefined,
                },
                '&.Mui-disabled fieldset': {
                  borderColor: isBusinessReasonRequired && !businessReason?.name ? 'error.main' : undefined,
                },
              },
            }}
            fullWidth
            placeholder="Select business reason"
            disabled
            required={isBusinessReasonRequired}
          />
        </Box>
        <TextField
          placeholder="Put down your internal note"
          multiline
          minRows={5}
          maxRows={5}
          label="Internal notes"
          variant="outlined"
          value={instance?.internal_notes ?? ''}
          fullWidth
          disabled
          sx={{ mt: 2 }}
        />
      </Box>
    </Section>
  );
};
